import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ClipboardList,
  CreditCard,
  ExternalLink,
  History,
  MapPin,
  PackageCheck,
  Printer,
  Save,
  Truck,
} from "lucide-react";
import { api } from "../../services/api";
import { formatUsDateTime } from "../../utils/timezone";
import { InsightCard, StatusPill, WorkflowToolbar } from "./AdminPanelParts";
import { printPackingSlip } from "./packingSlip";
import { ToastStack, useToast } from "./useToast";
import {
  emptyEditForm,
  formatMoney,
  normalizeOrder,
  orderAddress,
  paymentTone,
  statusTone,
} from "./orderUtils";

function copyText(value) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(value);
  const input = document.createElement("input");
  input.value = value;
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  document.body.removeChild(input);
  return Promise.resolve();
}

function formatSelectedOptions(options = {}) {
  return Object.entries(options || {})
    .filter(([, value]) => value)
    .map(([name, value]) => `${name}: ${value}`)
    .join(" / ");
}

function TimelineItem({ active, title, text }) {
  return (
    <div className="flex gap-3">
      <div className={`mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-black ${active ? "bg-[#4391d1] text-white" : "bg-gray-100 text-gray-400"}`}>
        {active ? "✓" : ""}
      </div>
      <div>
        <p className="font-black text-gray-900">{title}</p>
        <p className="mt-1 text-sm font-semibold text-gray-500">{text}</p>
      </div>
    </div>
  );
}

export default function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [trackingForm, setTrackingForm] = useState({ carrier: "UPS", trackingNumber: "" });
  const { dismiss, toast, toasts } = useToast();

  const loadOrder = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/orders/${orderId}`);
      const nextOrder = normalizeOrder(data.order);
      setOrder(nextOrder);
      setCustomerHistory((data.customerOrders || []).map(normalizeOrder));
      setEditForm({
        status: nextOrder.status || "processing",
        paymentStatus: nextOrder.paymentStatus || "paid",
        channel: nextOrder.channel || "direct",
        fraudFlag: Boolean(nextOrder.fraudFlag),
        adminNotes: nextOrder.adminNotes || "",
        line1: nextOrder.shippingAddress?.line1 || "",
        city: nextOrder.shippingAddress?.city || "",
        state: nextOrder.shippingAddress?.state || "",
        postalCode: nextOrder.shippingAddress?.postalCode || "",
        country: nextOrder.shippingAddress?.country || "US",
      });
      setTrackingForm({
        carrier: nextOrder.carrier || "UPS",
        trackingNumber: nextOrder.trackingNumber || "",
      });
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [orderId, toast]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const metrics = useMemo(() => {
    if (!order) return { itemCount: 0, subtotal: 0, total: 0, customerOrders: 0 };
    return {
      itemCount: order.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
      subtotal: formatMoney(order.subtotal),
      total: formatMoney(order.total),
      customerOrders: customerHistory.length,
    };
  }, [customerHistory.length, order]);

  const updateEditForm = (event) => {
    const { checked, name, type, value } = event.target;
    setEditForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const saveOrder = async () => {
    if (!order?._id) return;
    setSaving(true);
    try {
      const payload = {
        status: editForm.status,
        paymentStatus: editForm.paymentStatus,
        channel: editForm.channel,
        fraudFlag: editForm.fraudFlag,
        adminNotes: editForm.adminNotes,
        shippingAddress: {
          ...order.shippingAddress,
          line1: editForm.line1,
          city: editForm.city,
          state: editForm.state,
          postalCode: editForm.postalCode,
          country: editForm.country,
        },
      };
      const data = await api.patch(`/orders/${order._id}`, payload);
      setOrder(normalizeOrder(data.order));
      toast(`Saved ${data.order.orderNumber}`, "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const markShipped = async () => {
    if (!order?._id || !trackingForm.trackingNumber) {
      toast("Tracking number is required", "error");
      return;
    }
    try {
      const data = await api.patch(`/orders/${order._id}/ship`, trackingForm);
      setOrder(normalizeOrder(data.order));
      setEditForm((current) => ({ ...current, status: "shipped" }));
      toast(`Marked ${data.order.orderNumber} shipped`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const handlePrintPackingSlip = () => {
    if (!order) return;
    printPackingSlip(order);
    toast(`Packing slip generated for ${order.orderNumber}`, "success");
  };

  const createInvoiceLink = async () => {
    if (!order?._id) return;
    try {
      const data = await api.post("/invoices", {
        orderId: order._id,
        purpose: "paypal_payment",
        status: "sent",
      });
      await copyText(data.invoice.paymentLink);
      toast(`Invoice link created and copied: ${data.invoice.invoiceNumber}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  if (loading) {
    return <div className="p-6 md:p-10 text-sm font-bold text-gray-500">Loading order...</div>;
  }

  if (!order) {
    return (
      <div className="p-6 md:p-10">
        <h1 className="text-2xl font-black text-gray-900">Order not found</h1>
        <button className="mt-4 rounded-xl bg-gray-950 px-4 py-3 text-sm font-black text-white" onClick={() => navigate("/dashboard/orders")} type="button">Back to orders</button>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className="mb-8 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <Link className="mb-4 inline-flex items-center gap-2 text-sm font-black text-[#4391d1]" to="/dashboard/orders">
            <ArrowLeft size={16} /> Back to orders
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-black text-gray-900">{order.orderNumber}</h1>
            <StatusPill tone={statusTone(order.status)}>{order.status}</StatusPill>
            <StatusPill tone={paymentTone(order.paymentStatus)}>{order.paymentStatus}</StatusPill>
            {order.fraudFlag && <StatusPill tone="bad">Fraud flag</StatusPill>}
          </div>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">
            {order.user?.name} • {order.user?.email} • {order.channel}
          </p>
        </div>
        <WorkflowToolbar
          actions={[
            { label: "Save Changes", primary: true, onClick: saveOrder },
            { label: "Print Packing Slip", onClick: handlePrintPackingSlip },
            { label: "Create Invoice Link", onClick: createInvoiceLink },
            { label: "Refresh", onClick: loadOrder },
          ]}
        />
      </div>

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <InsightCard title="Items" value={metrics.itemCount} note="Total units in this order" />
        <InsightCard title="Subtotal" value={metrics.subtotal} note="Before shipping and tax" tone="money" />
        <InsightCard title="Total" value={metrics.total} note="Customer order total" tone="good" />
        <InsightCard title="Customer Orders" value={metrics.customerOrders} note="Recent history loaded" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_420px]">
        <div className="space-y-6">
          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <ClipboardList size={18} className="text-[#4391d1]" />
                <h2 className="text-lg font-black text-gray-900">Order Items</h2>
              </div>
              <span className="text-xs font-black uppercase text-gray-400">{order.items.length} line item{order.items.length === 1 ? "" : "s"}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                  <tr>{["Item", "SKU", "Qty", "Price", "Line total"].map((column) => <th className="px-4 py-3 font-black" key={column}>{column}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {order.items.map((item) => (
                    <tr key={`${item.sku}-${item.name}`}>
                      <td className="min-w-72 px-4 py-4 font-bold text-gray-900">
                        {item.name}
                        {formatSelectedOptions(item.selectedOptions) && (
                          <p className="mt-1 text-xs font-bold text-[#4391d1]">{formatSelectedOptions(item.selectedOptions)}</p>
                        )}
                      </td>
                      <td className="px-4 py-4 text-gray-500">{item.sku || "N/A"}</td>
                      <td className="px-4 py-4 font-black">{item.quantity}</td>
                      <td className="px-4 py-4">{formatMoney(item.price)}</td>
                      <td className="px-4 py-4 font-black">{formatMoney(item.price * item.quantity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center gap-2">
              <CreditCard size={18} className="text-[#4391d1]" />
              <h2 className="text-lg font-black text-gray-900">Payment & Totals</h2>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl bg-gray-50 p-4">
                <p className="text-xs font-black uppercase text-gray-400">Payment provider</p>
                <p className="mt-2 font-black capitalize text-gray-900">{order.paymentProvider || "manual"}</p>
                {order.paypalOrderId && <p className="mt-1 text-xs font-bold text-gray-500">PayPal order: {order.paypalOrderId}</p>}
                {order.paypalCaptureId && <p className="mt-1 text-xs font-bold text-gray-500">Capture: {order.paypalCaptureId}</p>}
              </div>
              <div className="rounded-xl bg-gray-50 p-4 text-sm">
                <div className="flex justify-between py-1"><span>Subtotal</span><strong>{formatMoney(order.subtotal)}</strong></div>
                <div className="flex justify-between py-1"><span>Shipping</span><strong>{formatMoney(order.shipping)}</strong></div>
                <div className="flex justify-between py-1"><span>Tax</span><strong>{formatMoney(order.tax)}</strong></div>
                <div className="mt-2 flex justify-between border-t border-gray-200 pt-3 text-lg"><span>Total</span><strong>{formatMoney(order.total)}</strong></div>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center gap-2">
              <History size={18} className="text-[#4391d1]" />
              <h2 className="text-lg font-black text-gray-900">Customer History</h2>
            </div>
            {customerHistory.length === 0 && <p className="text-sm font-semibold text-gray-500">No additional history loaded.</p>}
            <div className="grid gap-3">
              {customerHistory.map((historyOrder) => (
                <Link className="flex items-center justify-between gap-4 rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm hover:border-blue-200 hover:bg-blue-50" key={historyOrder._id} to={`/dashboard/orders/${historyOrder._id}`}>
                  <div>
                    <p className="font-black text-gray-900">{historyOrder.orderNumber}</p>
                    <p className="mt-1 text-xs font-bold text-gray-500">{historyOrder.status} • {historyOrder.paymentStatus}</p>
                  </div>
                  <strong className="text-gray-900">{formatMoney(historyOrder.total)}</strong>
                </Link>
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-black text-gray-900">Edit Order</h2>
                <p className="mt-1 text-xs font-bold text-gray-400">Status, payment, channel, and internal notes</p>
              </div>
              {editForm.fraudFlag && <AlertTriangle className="text-red-500" size={20} />}
            </div>
            <div className="grid gap-3">
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Status</span>
                <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-bold" name="status" onChange={updateEditForm} value={editForm.status}>
                  <option value="pending">Pending</option><option value="processing">Processing</option><option value="shipped">Shipped</option><option value="delivered">Delivered</option><option value="cancelled">Cancelled</option><option value="returned">Returned</option>
                </select>
              </label>
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Payment</span>
                <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-bold" name="paymentStatus" onChange={updateEditForm} value={editForm.paymentStatus}>
                  <option value="paid">Paid</option><option value="unpaid">Unpaid</option><option value="failed">Failed</option><option value="refunded">Refunded</option>
                </select>
              </label>
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Channel</span>
                <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-bold" name="channel" onChange={updateEditForm} value={editForm.channel}>
                  <option value="direct">Direct</option><option value="facebook_marketplace">Facebook market place</option><option value="ebay">eBay</option><option value="bobbinusa">Bobbin USA</option>
                </select>
              </label>
              <label className="flex items-center gap-2 rounded-xl bg-red-50 px-3 py-3 text-sm font-bold text-red-700">
                <input checked={editForm.fraudFlag} name="fraudFlag" onChange={updateEditForm} type="checkbox" /> Fraud flag
              </label>
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Admin notes</span>
                <textarea className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-semibold" name="adminNotes" onChange={updateEditForm} rows="4" value={editForm.adminNotes} />
              </label>
            </div>
            <button className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-gray-950 px-4 py-3 text-sm font-bold text-white disabled:opacity-60" disabled={saving} onClick={saveOrder} type="button">
              <Save size={16} /> {saving ? "Saving..." : "Save Changes"}
            </button>
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2 font-black text-gray-900"><MapPin size={16} /> Shipping Address</div>
            <p className="mb-4 rounded-xl bg-gray-50 p-3 text-sm font-semibold text-gray-600">{orderAddress(order) || "No address on file"}</p>
            <div className="grid gap-3">
              {[
                ["line1", "Address"],
                ["city", "City"],
                ["state", "State"],
                ["postalCode", "Postal code"],
                ["country", "Country"],
              ].map(([field, label]) => (
                <label key={field}>
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">{label}</span>
                  <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-bold" name={field} onChange={updateEditForm} value={editForm[field]} />
                </label>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 font-black text-gray-900"><Truck size={16} /> Fulfillment</div>
              {order.shippingAddress?.shippingLabel && <StatusPill tone="info">{order.shippingAddress.shippingLabel}</StatusPill>}
            </div>
            {order.shippingLabelUrl && (
              <a className="mb-4 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white" href={order.shippingLabelUrl} target="_blank" rel="noreferrer">
                <ExternalLink size={16} />
                Open Shippo Label
              </a>
            )}
            {!order.shippingLabelUrl && (
              <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50 p-4">
                <p className="text-sm font-black text-blue-950">Shipping label needed</p>
                <p className="mt-1 text-xs font-semibold leading-5 text-blue-800">
                  Save any address corrections, then use Shipping Center to compare Shippo rates and purchase the label.
                </p>
                <Link
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#4391d1] px-3 py-2.5 text-xs font-black text-white"
                  to={`/dashboard/shipping?order=${order._id}`}
                >
                  <Truck size={14} /> Create label in Shipping Center
                </Link>
              </div>
            )}
            <div className="grid gap-3">
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Carrier</span>
                <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-bold" onChange={(event) => setTrackingForm((current) => ({ ...current, carrier: event.target.value }))} value={trackingForm.carrier}>
                  <option>UPS</option><option>FedEx</option><option>USPS</option><option>Pirate Ship</option>
                </select>
              </label>
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Tracking number</span>
                <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-bold" onChange={(event) => setTrackingForm((current) => ({ ...current, trackingNumber: event.target.value }))} value={trackingForm.trackingNumber} />
              </label>
              <button className="rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-bold text-white" onClick={markShipped} type="button">Assign Tracking & Mark Shipped</button>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center gap-2 font-black text-gray-900"><PackageCheck size={16} /> Order Timeline</div>
            <div className="space-y-5">
              <TimelineItem active title="Order created" text={formatUsDateTime(order.createdAt)} />
              <TimelineItem active={order.paymentStatus === "paid"} title="Payment received" text={order.paymentStatus === "paid" ? "Payment is marked paid." : "Payment still needs attention."} />
              <TimelineItem active={["processing", "shipped", "delivered"].includes(order.status)} title="Processing" text="Order is ready for fulfillment work." />
              <TimelineItem active={["shipped", "delivered"].includes(order.status)} title="Shipped" text={order.trackingNumber ? [order.carrier, order.trackingNumber].filter(Boolean).join(" ") : "Tracking not assigned yet."} />
            </div>
          </section>

          <button className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold text-gray-800" onClick={handlePrintPackingSlip} type="button"><Printer size={16} /> Print Packing Slip</button>
          <button className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-bold text-white" onClick={createInvoiceLink} type="button">Create PayPal Invoice Link</button>
        </aside>
      </div>
    </div>
  );
}
